import { CheckCircleOutline, PointOfSale } from "@mui/icons-material";
import {
    Alert,
    AlertTitle,
    Button,
    CircularProgress,
    Drawer,
    TextField, Grid, Typography
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { PosContext } from "../../shared/PosContext";
import {
    useCloseCash,
    useGetSalesData,
    useSearchPOSOpening,
} from "../../shared/hooks/usePOSData";
import { dateFormat, isDateBeforeToday } from "../../utils/dateUtils";
import SimpleWidgetsHeader from "../SimpleWidgetsHeader/SimpleWidgetsHeader";
import SimpleWidgetsInput from "../SimpleWidgetsInput/SimpleWidgetsInput";
import { useStyles } from "./styles";

const OpeningEntries = ({ open, onClose, openingEntry }) => {
    const classes = useStyles();
    const { refetch: refetchOpeneningEntries } = useSearchPOSOpening();

    const closeCashMutation = useCloseCash();

    const [cashCount, setCashCount] = useState("");
    const [visaCount, setVisaCount] = useState("");
    const [totalVisa, setTotalVisa] = useState(0);
    const [totalCash, setTotalCash] = useState(0);
    const [openingBalance, setOpeningBalance] = useState(0);

    const { posProfiles } = useContext(PosContext);

    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [expectedTotal, setExpectedTotal] = useState(null);
    const [initialCashCount, setInitialCashCount] = useState(null);
    const [countDiff, setCountDiff] = useState(null);
    const [cashCloseLoading, setCashCloseLoading] = useState(false);

    const [salesParams, setSalesParams] = useState({});
    const { data: getSalesData, isLoading: salesLoading } =
        useGetSalesData(salesParams);

    const initialDenominations = {
        '200': 0,
        '100': 0,
        '50': 0,
        '20': 0,
        '10': 0,
        '5': 0,
        '2': 0,
        '1': 0,
    };

    const [denominations, setDenominations] = useState({
        '200': 0,
        '100': 0,
        '50': 0,
        '20': 0,
        '10': 0,
        '5': 0,
        '2': 0,
        '1': 0
    });

    const totalCashCount = Object.entries(denominations).reduce((total, [denomination, amount]) => {
        return total + Number(denomination) * Number(amount);
    }, 0);

    const handleDenominationChange = (denomination) => (event) => {
        setDenominations({
            ...denominations,
            [denomination]: Number(event.target.value)
        });
    };

    useEffect(() => {
        refetchOpeneningEntries();
    }, []);

    useEffect(() => {
        if (posProfiles && openingEntry) {
            const payload = {
                company: posProfiles[0].company,
                pos_profile: posProfiles[0].name,
                // start: openingEntry.period_start_date,
                // end: getCurrentDate(),
                opening_amount: 0,
            };
            setSalesParams(payload);
        }
    }, [posProfiles, openingEntry]);

    const totalOpenOrders = useMemo(() => {
        if (getSalesData) {
            return getSalesData;
        }
        return {};
    }, [getSalesData]);

    const handleClose = () => {
        onClose?.();
    };

    const setInitialValues = () => {
        const totalCash = totalOpenOrders?.total_cash
            ? Number(totalOpenOrders?.total_cash)
            : 0;
        const totalVisa = totalOpenOrders?.total_credit_card
            ? Number(totalOpenOrders?.total_credit_card)
            : 0;

        const openingBalance = openingEntry?.balance_details?.find(
            (i) => i.mode_of_payment === "Cash"
        )?.opening_amount;

        setOpeningBalance(Number(openingBalance));
        setTotalCash(totalCash);
        setTotalVisa(totalVisa);

        setExpectedTotal(
            Number(openingBalance) + Number(totalCash) + Number(totalVisa)
        );

        setInitialCashCount(cashCount);
    }

    // instead of waiting for the total cash to get eval we will just do things here.
    useEffect(() => {
        if (getSalesData) {
            setInitialValues();
        }
    }, [getSalesData]);

    const handleCloseCash = () => {
        setCashCloseLoading(true);
        closeCashMutation.mutate(
            {
                company: posProfiles[0].company,
                pos_profile: posProfiles[0].name,
                expected_amount: openingBalance + totalCash + totalVisa,
                amount_in_register: cashCount,
                opening_amount: 0,
            },
            {
                onSuccess: () => {
                    console.log('vilooo');
                    onClose();
                },
                onSettled: () => {
                    console.log('onSettled');
                    clear();
                },
            }
        );
    };

    // Step 3: Use the useEffect hook to watch for changes to the state variable
    useEffect(() => {
        setCashCount(totalCashCount);
        if (totalCashCount > 0) {
            const diff =
                Number(openingBalance) +
                Number(totalCash) +
                Number(totalVisa) -
                Number(totalCashCount);
            setCountDiff(Math.round(diff));
        } else {
            setCountDiff(0);
        }
        setInitialCashCount(cashCount);
    }, [denominations]); // The second argument is the dependency array


    const clear = () => {
        refetchOpeneningEntries();
        setCashCount("");
        setCashCloseLoading(false);
        setDenominations(initialDenominations);
        setExpectedTotal(null);
    };

    return (
        <React.Fragment>
            <Drawer
                className={classes.Drawer}
                anchor="right"
                open={open}
                onClose={handleClose}
            >
                <div className={classes.DrawerWrapper}>
                    <SimpleWidgetsHeader
                        Icon={PointOfSale}
                        title="POS Cash Closing"
                        onClose={onClose}
                    />

                    {isDateBeforeToday(openingEntry?.period_start_date) && (
                        <Alert severity="warning">
                            <AlertTitle>Warning</AlertTitle>
                            Current POS Cash Opening Entry's date is{" "}
                            <strong>
                                {dateFormat(openingEntry.period_start_date)}
                            </strong>
                        </Alert>
                    )}

                    {openingEntry && (
                        <div className={classes.OpeningEntriesWrapper}>
                            <Grid container spacing={2}>
                                {Object.keys(denominations)
                                    .map(Number)
                                    .sort((a, b) => b - a)
                                    .map((denomination) => (
                                        <Grid item xs={6} key={denomination}>
                                            <TextField
                                                label={`${denomination} ₪`}
                                                type="number"
                                                value={denominations[denomination]}
                                                onChange={handleDenominationChange(denomination)}
                                                fullWidth
                                                variant="standard"
                                                inputProps={{ min: 0 }}
                                            />
                                        </Grid>
                                    ))}
                            </Grid>

                            {expectedTotal && (
                                <div>
                                    <Typography variant="body1">
                                        {totalCashCount && countDiff === 0 ? (
                                            <CheckCircleOutline style={{ color: "green" }} />
                                        ) : (
                                            `Total: ${totalCashCount}`
                                        )}
                                    </Typography>
                                    <Button
                                        className={classes.ActionButton}
                                        variant="contained"
                                        disabled={
                                            totalCashCount > 0
                                                ? false
                                                : true
                                        }
                                        onClick={handleCloseCash}
                                    >
                                        {cashCloseLoading ? <CircularProgress /> : "Close Cash"}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Drawer>
        </React.Fragment>
    );
};

export default OpeningEntries;
